import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container, Row, Column, List, Icon } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, Image, CTA } from '../../components';
import TabContent from '../../components/template/energy-efficiency/tab-content';
import FAQ from '../../components/common/faq';
import DynamicComponent from '../../components/common/dynamic-component';
import CardSection from '../../components/common/card-section';
import './index.css';
import { isValidArray } from '../../components/utilities/validations';

const EnergyEfficiency = (props) => {
	const pageData = props.data.contentstackEnergyEfficiency;

	return (
		<Layout id='template-EnergyEfficiency'>
			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			<div className='relative grid grid-cols-1 md:grid-cols-[1.9fr_1.1fr] xl:grid-cols-[1.975fr_1.025fr]'>
				{pageData.hero.image?.[0] && (
					<Image
						className='col-start-1 row-start-1 block aspect-[3/2] h-full max-h-[750px] w-full max-w-full'
						data={pageData.hero.image?.[0]}
					/>
				)}
				{pageData.hero.icon?.url && (
					<div className='col-start-1 row-start-1 self-end pb-5 pl-6 md:pb-4 md:pl-8 xl:pb-8 xl:pl-16'>
						<Image
							className='h-[60px] w-[148px] md:h-[66px] md:w-[164px] xl:h-[72px] xl:w-[179px]'
							data={{ image: pageData.hero.icon }}
						/>
					</div>
				)}

				<div className='col-start-1 row-start-2 flex flex-col justify-center bg-black py-4 md:col-start-2 md:row-start-1 md:h-full md:py-6'>
					<div className='px-6 md:px-8 xl:px-10'>
						{pageData.hero.heading && (
							<h1
								className='mb-2 text-white [&_a]:text-white [&_a]:decoration-current'
								dangerouslySetInnerHTML={{ __html: pageData.hero.heading }}
							/>
						)}
						{pageData.hero.description && (
							<div
								className='text-base font-normal text-white md:text-lg xl:text-xl [&_a]:text-white [&_a]:decoration-current'
								dangerouslySetInnerHTML={{ __html: pageData.hero.description }}
							/>
						)}
					</div>
				</div>
			</div>

			{isValidArray(pageData?.modular_blocks) &&
				pageData.modular_blocks.map((block, index) => {
					if (block.statistics != null) {
						return (
							<div
								className='py-[60px] md:px-[90px] xl:px-[195px] xl:py-20'
								key={index}
							>
								<Container>
									{block.statistics.heading && (
										<h2
											className='text-h1 m-0 pb-7'
											dangerouslySetInnerHTML={{ __html: block.statistics.heading }}
										/>
									)}
									{block.statistics.card_stats?.[0] && (
										<Row>
											{block.statistics.card_stats.map((stat, index) => (
												<Column
													key={index}
													xs={12}
													md='fluid'
												>
													{stat.statistic && (
														<div
															style={{ backgroundColor: stat.container_hex_value }}
															className='mb-1.5 flex h-[54px] flex-row items-center justify-center border-gray-10 px-3 text-center text-xs font-bold uppercase text-gray-90 md:h-[140px] md:flex-col [&>p>span>span]:font-[250] [&_p>span]:px-1.5 [&_p]:m-0 [&_span]:text-3xl [&_span]:font-extrabold [&_span]:md:text-5xl'
															dangerouslySetInnerHTML={{ __html: stat.statistic }}
														/>
													)}
												</Column>
											))}
										</Row>
									)}
									{block.statistics.rte_stats?.[0] && (
										<div>
											{block.statistics.rte_stats.map((stat, index) => (
												<div key={index}>
													{stat.material_symbol && (
														<Icon
															name={stat.material_symbol}
															className='pt-7 text-black md:pt-8'
															size={24}
														/>
													)}
													{stat.statistic && (
														<div
															className='text-base [&_strong]:pb-[6px] [&_strong]:text-22px [&_strong]:text-xl [&_strong]:text-black [&_strong]:md:pb-2'
															dangerouslySetInnerHTML={{ __html: stat.statistic }}
														/>
													)}
												</div>
											))}
										</div>
									)}
								</Container>
							</div>
						);
					}

					if (block.banner != null) {
						return (
							<div
								style={{ backgroundColor: block.banner.background_hex_value }}
								className='py-11 md:py-[52px] xl:px-[8.33%]'
								key={index}
							>
								<Container>
									{block.banner && (
										<div
											key={index}
											xs={12}
											md='fluid'
										>
											{block.banner.subheading && (
												<div
													className='text-overline pb-3 text-gray-5 [&_a]:text-white [&_a]:decoration-current'
													dangerouslySetInnerHTML={{ __html: block.banner.subheading }}
												/>
											)}
											{block.banner.statistic && (
												<div
													className='text-32px leading-8 tracking-[-1.5px] text-white md:text-[40px] md:leading-[42px] md:tracking-[-0.5px] [&_a]:text-white [&_a]:decoration-current [&_span]:text-primary'
													dangerouslySetInnerHTML={{ __html: block.banner.statistic }}
												/>
											)}
										</div>
									)}
								</Container>
							</div>
						);
					}

					if (block.text_carousel != null) {
						return (
							<TabContent
								key={index}
								content={block.text_carousel}
							/>
						);
					}

					if (block.cards != null) {
						return (
							<CardSection
								data={block.cards}
								key={index}
							/>
						);
					}

					if (block.general_content != null) {
						return (
							<div
								key={index}
								style={{ backgroundColor: block.general_content.background_hex_value }}
							>
								<Container>
									<div className='pb-12'>
										<Row align='center'>
											{block.general_content.image?.[0] && (
												<Column
													xs={12}
													md={6}
													className='mb-5 md:mb-0'
												>
													<Image
														data={block.general_content.image[0]}
														className='aspect-[2/1]'
													/>
												</Column>
											)}

											<Column
												xs={12}
												md='fluid'
											>
												<div className='bg-gray-3 !px-4 py-6 xl:py-8'>
													{block.general_content.heading && (
														<div
															className='text-h3 m-0 [&>p>span:first-child]:fill-gray-42 [&>p>span:first-child]:text-gray-42 [&>p>span:nth-child(2)]:pl-1 [&>p>span:nth-child(2)]:md:pl-2.5 [&_p]:flex [&_p]:items-center [&_p]:justify-center'
															dangerouslySetInnerHTML={{ __html: block.general_content.heading }}
														/>
													)}
													{block.general_content.description && (
														<div
															className='px-3 pt-3 text-center text-base text-gray-90'
															dangerouslySetInnerHTML={{ __html: block.general_content.description }}
														/>
													)}
													{block.general_content.cta?.[0] && (
														<CTA
															data={block.general_content.cta[0]}
															className='mt-6 max-md:p-3 md:mt-8'
														/>
													)}
												</div>
											</Column>
										</Row>
									</div>
								</Container>
							</div>
						);
					}

					if (block.rta != null) {
						return (
							<div
								key={index}
								className='perfRta bg-gray-5'
								style={{ backgroundColor: block.rta.background_hex_value }}
							>
								<Container className={`grid grid-cols-1 max-md:px-0 md:grid-cols-2 md:gap-x-4 md:pr-0 xl:gap-x-28 ${block.rta.image_position === 'right' ? 'xl:grid-cols-[1fr_535px]' : 'xl:grid-cols-[535px_1fr]'}`}>
									{block.rta.image?.[0] && (
										<Image
											data={block.rta.image[0]}
											className={`col-start-1 row-start-1 !block aspect-[1.8/1] h-full md:aspect-auto ${block.rta.image_position === 'right' ? 'md:col-start-2' : ''}`}
										/>
									)}

									<div className={`col-start-1 row-start-2 p-6 md:row-start-1 md:flex md:flex-col md:justify-center md:px-0 ${block.rta.image_position === 'right' ? 'md:col-start-1' : ''}`}>
										{block.rta.heading && <h2 className='m-0'>{block.rta.heading}</h2>}

										{block.rta.description && (
											<div
												className='text-subtitle-2 py-2.5'
												dangerouslySetInnerHTML={{ __html: block.rta.description }}
											/>
										)}

										{block.rta.cta?.[0] && (
											<List
												bullets='none'
												className='mt-4 flex flex-wrap gap-y-2.5 xl:gap-x-4'
											>
												{block.rta.cta.map((cta, index) => (
													<li
														key={index}
														className='max-w-full flex-12/12 md:flex-8/12 xl:flex-1'
													>
														<CTA
															data={cta}
															wide
														/>
													</li>
												))}
											</List>
										)}
									</div>
								</Container>
							</div>
						);
					}

					if (block.faqs?.faqs?.[0]) {
						return (
							<div
								id={block.faqs.anchor_text || null}
								key={index}
							>
								<Container width={1200}>
									<h2>{block.faqs.heading}</h2>
									<FAQ data={block.faqs.faqs} />
								</Container>
							</div>
						);
					}

					if (block.dynamic?.dynamic?.[0]) {
						return (
							<DynamicComponent
								data={block.dynamic}
								key={index}
							/>
						);
					}
				})}
		</Layout>
	);
};

EnergyEfficiency.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default EnergyEfficiency;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => <SEO {...data.contentstackEnergyEfficiency.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackEnergyEfficiency(id: { eq: $uid }) {
			url
			breadcrumbs {
				...Breadcrumbs
			}
			id
			hero {
				description
				heading
				icon {
					url
					gatsbyImageData
				}
				image {
					...Image
					image {
						url
						gatsbyImageData(quality: 90)
					}
				}
			}
			modular_blocks {
				cards {
					...CardSection
				}
				dynamic {
					heading
					dynamic {
						...Dynamic
					}
				}
				general_content {
					background_hex_value
					container_hex_value
					description
					heading
					is_container
					text_justify
				}
				rta {
					background_hex_value
					cta {
						...CTA
					}
					description
					heading
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90)
						}
					}

					image_position
				}
				statistics {
					heading
					card_stats {
						container_hex_value
						statistic
					}
					rte_stats {
						material_symbol
						statistic
					}
				}
				banner {
					background_hex_value
					statistic
					subheading
				}
				text_carousel {
					background_hex_value
					heading
					description
					cta {
						...CTA
					}
					layout_options
					content_sections {
						description
						heading
						image {
							...Image
							image {
								url
								gatsbyImageData(quality: 90)
							}
						}
					}
					cta {
						...CTA
					}
				}
			}
			seo {
				...SEO
			}
		}
	}
`;
